import React, { useMemo, useState } from 'react';
import Panel from './Panel';
import { EXECUTED_SURVEYS_ACTIONS } from './panelActions';
import useContextMenu from '../hooks/useContextMenu';
import ContextMenu from './ContextMenu';
import { openExplorer as launchExplorer } from '../utils/explorerLaunch';

const STATUS_OPTIONS = ['all', 'in-process', 'open', 'closed'];

function formatStatusLabel(value) {
    if (value === 'all') return 'All';
    if (value === 'in-process') return 'In Process';
    return value.charAt(0).toUpperCase() + value.slice(1);
}

const COLUMNS = [
    { key: 'name', label: 'Survey', width: 'minmax(200px, 2.4fr)', align: 'left' },
    { key: 'status', label: 'Status', width: '82px', align: 'left' },
    {
        key: 'linkedSent',
        label: 'Linked/Sent',
        width: '92px',
        align: 'right',
        title: 'Linked = recipient is linked to this deployment. Sent = dispatch has been triggered.',
    },
    {
        key: 'n',
        label: 'n (y/x)',
        width: '78px',
        align: 'right',
        title: 'Final n = responding / sent (y/x). Anonymous link deployments show completed / started sessions.',
    },
    { key: 'completed', label: 'Completed', width: '78px', align: 'right' },
    { key: 'rate', label: 'Rate', width: '58px', align: 'right' },
    { key: 'lastActivity', label: 'Last Activity', width: '118px', align: 'left' },
    { key: 'channel', label: 'Channel', width: '80px', align: 'left', secondary: true },
    { key: 'language', label: 'Language', width: '80px', align: 'left', secondary: true },
    { key: 'created', label: 'Created', width: '118px', align: 'left', secondary: true },
    { key: 'started', label: 'Started', width: '68px', align: 'right', secondary: true },
    { key: 'abandoned', label: 'Abandoned', width: '78px', align: 'right', secondary: true },
];

function firstFinite(...values) {
    for (const value of values) {
        const number = Number(value);
        if (Number.isFinite(number)) return number;
    }
    return 0;
}

function timestampValue(...values) {
    for (const value of values) {
        if (!value) continue;
        const time = new Date(value).getTime();
        if (Number.isFinite(time)) return time;
    }
    return 0;
}

function formatDate(time) {
    if (!time) return '—';
    const date = new Date(time);
    if (Number.isNaN(date.getTime())) return '—';
    const month = date.toLocaleString(undefined, { month: 'short' });
    const day = date.getDate();
    const hour24 = date.getHours();
    const hour12 = (hour24 % 12) || 12;
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const suffix = hour24 >= 12 ? 'PM' : 'AM';
    return `${month} ${day} ${hour12}:${minutes}${suffix}`;
}

function getStatus(deployment) {
    const raw = String(deployment.status || '').toLowerCase();
    if (deployment.is_open === true || raw === 'open' || raw === 'active') return 'open';
    if (deployment.is_open === false || raw === 'closed' || raw === 'inactive') return 'closed';
    return raw || 'closed';
}

function normalizeDeployment(deployment, surveys = [], counts = null) {
    // WP3: when the shared n = y/x counts from a /undeploy (or enriched
    // deployment) response are available, they win — they are the same numbers
    // the server's completion check and the Explorer use, so the Dispatched
    // history can never disagree with them.
    const c = counts || {};
    const linked = firstFinite(
        deployment.linkedRecipientCount,
        deployment.linked_recipient_count,
        deployment.linked_count,
        deployment.assigned_count,
        deployment.recipient_count,
        deployment.total_recipients
    );
    const sent = firstFinite(
        c.sent_count,
        deployment.sentRecipientCount,
        deployment.sent_recipient_count,
        deployment.sent_count,
        deployment.outreach_sent_count,
        deployment.invitation_sent_count,
        deployment.sent
    );
    const completed = firstFinite(
        c.completed_count,
        deployment.completedRecipientCount,
        deployment.completed_recipient_count,
        deployment.completed_count,
        deployment.completed,
        deployment.responses_completed,
        deployment.completion_count
    );
    const lastActivity = timestampValue(
        deployment.last_activity_at,
        deployment.lastActivityAt,
        deployment.last_response_at,
        deployment.updated_at,
        deployment.created_at
    );
    const lookedUp = (surveys || []).find(s => s.survey_id === deployment.survey_id);
    const name = deployment.survey_name
        || deployment.name
        || deployment.title
        || lookedUp?.name
        || lookedUp?.title
        || deployment.survey_id
        || deployment.deployment_id
        || '—';
    const surveyId = deployment.survey_id || deployment.surveyId || null;
    const slug = deployment.deploy_slug || deployment.deployment_slug || deployment.slug || deployment.deployment_id || '—';
    const status = getStatus(deployment);

    const started = firstFinite(
        c.started_count,
        deployment.started_count,
        deployment.in_progress_count,
        deployment.started
    );
    // y (responding recipients). Falls back to completed when no explicit
    // responding count is carried (the plain deployments list has none).
    const responding = firstFinite(
        c.responding,
        deployment.responding,
        deployment.responding_count,
        completed
    );
    // Anonymous link deployments have no fixed x (sent), so n is expressed as
    // completed/started sessions, per the shared n definition.
    const isAnonymous = (c.is_anonymous === true) || sent === 0;
    const hasCounts = sent > 0 || started > 0 || completed > 0;
    const n = !hasCounts
        ? '—'
        : (isAnonymous ? `${completed}/${started}` : `${responding}/${sent}`);

    return {
        deployment,
        id: deployment.deployment_id || deployment.id || slug,
        surveyId,
        name,
        slug,
        status,
        linked,
        sent,
        completed,
        responding,
        isAnonymous,
        n,
        rate: sent > 0 ? (completed / sent * 100).toFixed(0) + '%' : '—',
        rateValue: sent > 0 ? completed / sent : -1,
        lastActivity,
        channel: deployment.target_channel || deployment.channel || '—',
        language: deployment.language || deployment.survey_language || '—',
        created: timestampValue(deployment.created_at, deployment.createdAt),
        started,
        abandoned: firstFinite(c.abandoned_count, deployment.abandoned_count, deployment.dropout_count, deployment.abandoned),
    };
}

function openExplorer(deployment) {
    const deploymentId = deployment.deployment_id || deployment.id;
    if (!deploymentId) return;
    launchExplorer({
        surveyId: deployment.survey_id,
        deploymentId,
        tab: 'explore',
    }).catch(err => console.error('Open Explorer failed:', err));
}

function renderCell(row, col, options = {}) {
    const nameStyle = options.nameIndent
        ? { ...cellStyle, paddingLeft: 8 + options.nameIndent }
        : cellStyle;

    switch (col.key) {
        case 'name':
            return (
                <div title={row.name} style={nameStyle}>
                    <div style={{ fontWeight: 500 }}>{row.name}</div>
                    {row.surveyId && (
                        <div style={{ fontSize: 10, color: '#9AA0A6', fontFamily: 'monospace', marginTop: 1 }}>
                            {row.surveyId}
                        </div>
                    )}
                </div>
            );
        case 'status':
            return <div title={row.status} style={{ ...cellStyle, textTransform: 'capitalize', color: statusColor(row.status) }}>{row.status}</div>;
        case 'linkedSent':
            return (
                <div style={{ ...numberCellStyle, color: '#5F6368' }}>
                    {row.linked} / {row.sent}
                    {row.linked > row.sent && (
                        <span style={{ fontSize: 11, color: '#9AA0A6', marginLeft: 4 }}>
                            ({row.linked - row.sent} pending)
                        </span>
                    )}
                </div>
            );
        case 'n':
            return (
                <div
                    title={row.isAnonymous ? 'completed / started sessions (anonymous link)' : 'responding / sent (y / x)'}
                    style={numberCellStyle}
                >
                    {row.n}{row.isAnonymous && row.n !== '—' ? ' *' : ''}
                </div>
            );
        case 'completed':
            return <div style={numberCellStyle}>{row.completed}</div>;
        case 'rate':
            return <div style={numberCellStyle}>{row.rate}</div>;
        case 'lastActivity':
            return <div title={formatDate(row.lastActivity)} style={{ ...cellStyle, color: '#5F6368' }}>{formatDate(row.lastActivity)}</div>;
        case 'channel':
            return <div style={cellStyle}>{row.channel}</div>;
        case 'language':
            return <div style={cellStyle}>{row.language}</div>;
        case 'created':
            return <div title={formatDate(row.created)} style={{ ...cellStyle, color: '#5F6368' }}>{formatDate(row.created)}</div>;
        case 'started':
            return <div style={numberCellStyle}>{row.started}</div>;
        case 'abandoned':
            return <div style={numberCellStyle}>{row.abandoned}</div>;
        default:
            return <div style={cellStyle}>—</div>;
    }
}

export default function ExecutedSurveysPanel({
    search,
    setSearch,
    refresh,
    loading,
    selectedDeploymentId,
    setSelectedSurveyId,
    setSelectedDeploymentId,
    deployments,
    surveys,
    // Final n = y/x counts per deployment, captured from /undeploy responses.
    deploymentCounts = {},
    // Action handlers — wired by App.js (BUG-106).
    onRedeploy,
}) {
    const { openMenu, contextMenuProps } = useContextMenu();
    const [statusFilter, setStatusFilter] = useState('all');
    const [sort, setSort] = useState({ key: 'lastActivity', direction: 'desc' });
    const [visibleCols, setVisibleCols] = useState(() => {
        try {
            const stored = localStorage.getItem('console.executedSurveys.columns');
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    });
    const [showColConfig, setShowColConfig] = useState(false);
    // Grouped and collapsed on open. A survey deployed several times produces a
    // row per deployment, so the flat view opens with more rows than a reader
    // can take in, and the survey a row belongs to is only readable by matching
    // repeated names down the Survey column. One collapsed row per survey shows
    // the deployment count and the summed totals; expanding is one click.
    const [groupByParent, setGroupByParent] = useState(true);
    // Keyed by group; absent means collapsed, which is what a fresh panel wants.
    const [expandedGroups, setExpandedGroups] = useState({});

    const activeColumns = useMemo(() =>
        COLUMNS.filter(c => !c.secondary || visibleCols[c.key]),
        [visibleCols]
    );
    const activeGridTemplate = activeColumns.map(c => c.width).join(' ');

    const rows = useMemo(() => {
        const query = String(search || '').trim().toLowerCase();
        return (deployments || [])
            .map(d => normalizeDeployment(d, surveys, deploymentCounts[d.deployment_id || d.id]))
            .filter(row => {
                if (statusFilter === 'all') return true;
                if (statusFilter === 'in-process') {
                    return row.status === 'open' && row.sent > 0 && row.completed < row.sent;
                }
                return row.status === statusFilter;
            })
            .filter(row => {
                if (!query) return true;
                return `${row.name} ${row.slug}`.toLowerCase().includes(query);
            })
            .sort((a, b) => {
                let av = a[sort.key];
                let bv = b[sort.key];
                if (sort.key === 'linkedSent') {
                    av = a.linked;
                    bv = b.linked;
                }
                if (sort.key === 'rate') {
                    av = a.rateValue;
                    bv = b.rateValue;
                }
                if (typeof av === 'string') av = av.toLowerCase();
                if (typeof bv === 'string') bv = bv.toLowerCase();
                if (av < bv) return sort.direction === 'asc' ? -1 : 1;
                if (av > bv) return sort.direction === 'asc' ? 1 : -1;
                return 0;
            });
    }, [deployments, search, sort, statusFilter, surveys, deploymentCounts]);

    const groupedRows = useMemo(() => {
        if (!groupByParent) return null;
        const groups = new Map();
        for (const row of rows) {
            const key = row.surveyId || row.name;
            if (!groups.has(key)) groups.set(key, { key, name: row.name, surveyId: row.surveyId, children: [] });
            groups.get(key).children.push(row);
        }
        return Array.from(groups.values());
    }, [rows, groupByParent]);

    const handleSort = (key) => {
        setSort(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const handleSelect = (row) => {
        setSelectedDeploymentId(row.id);
        setSelectedSurveyId(row.surveyId);
    };

    // Build a derived actions array that swaps the stubbed onClick handlers
    // (defined in panelActions.js) for real ones that close over the currently
    // selected deployment. Same StagingArea pattern as BUG-104/105.
    const selectedRow = rows.find(r => r.id === selectedDeploymentId);
    const actions = EXECUTED_SURVEYS_ACTIONS.map(a => {
        switch (a.label) {
            case 'Re-deploy':
                return {
                    ...a,
                    onClick: () => selectedRow && onRedeploy?.(
                        selectedRow.surveyId,
                        selectedRow.id,
                        selectedRow.name,
                    ),
                };
            default:
                return a;
        }
    });

    // Per-row right-click menu items. Each handler closes over the row data
    // directly so right-clicking row A while row B is selected acts on A.
    const menuItems = (row) => [
        ...EXECUTED_SURVEYS_ACTIONS
            .filter(action => !action.hideWhen || !action.hideWhen({}))
            .map(action => {
                const rowOnClick = (() => {
                    switch (action.label) {
                        case 'Re-deploy': return () => onRedeploy?.(row.surveyId, row.id, row.name);
                        default:          return action.onClick;
                    }
                })();
                return {
                    label: action.label,
                    onClick: rowOnClick,
                    dimmed: action.requiresSelection && !row.id,
                };
            }),
        { separator: true },
        { label: 'Investigate in Explorer', onClick: () => openExplorer(row.deployment), dimmed: !row.id },
    ];

    return (
        <>
            <Panel
                title="Dispatched"
                icon="executed"
                actions={actions}
                selection={selectedDeploymentId}
                style={{ height: '100%', boxSizing: 'border-box', minWidth: 0 }}
            >
                <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name or slug"
                        style={{ flex: 1, minWidth: 0, height: 28, fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, padding: '0 8px' }}
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ height: 30, fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, background: '#fff' }}
                    >
                        {STATUS_OPTIONS.map(status => (
                            <option key={status} value={status}>{formatStatusLabel(status)}</option>
                        ))}
                    </select>
                    {refresh && (
                        <button
                            type="button"
                            onClick={refresh}
                            disabled={loading}
                            style={{ height: 30, padding: '0 10px', fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, background: '#fff', color: loading ? '#BDC1C6' : '#3C4043' }}
                        >
                            Refresh
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setGroupByParent(prev => !prev)}
                        style={{
                            height: 30,
                            padding: '0 10px',
                            fontSize: 12,
                            border: '1px solid #DADCE0',
                            borderRadius: 4,
                            background: groupByParent ? '#E8F0FE' : '#fff',
                            color: groupByParent ? '#1967D2' : '#3C4043',
                            cursor: 'pointer',
                        }}
                    >
                        {groupByParent ? '📁 Grouped' : '📋 Flat'}
                    </button>
                    <div style={{ position: 'relative' }}>
                        <button
                            type="button"
                            onClick={() => setShowColConfig(prev => !prev)}
                            style={{ height: 30, padding: '0 8px', fontSize: 12, border: '1px solid #DADCE0', borderRadius: 4, background: '#fff', cursor: 'pointer' }}
                            aria-label="Configure columns"
                        >
                            ⚙️
                        </button>
                        {showColConfig && (
                            <div style={{
                                position: 'absolute',
                                top: 34,
                                right: 0,
                                zIndex: 10,
                                background: '#fff',
                                border: '1px solid #DADCE0',
                                borderRadius: 6,
                                padding: 8,
                                minWidth: 160,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#5F6368', marginBottom: 6 }}>Show columns</div>
                                {COLUMNS.filter(c => c.secondary).map(col => (
                                    <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 0', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={!!visibleCols[col.key]}
                                            onChange={() => {
                                                const next = { ...visibleCols, [col.key]: !visibleCols[col.key] };
                                                setVisibleCols(next);
                                                try {
                                                    localStorage.setItem('console.executedSurveys.columns', JSON.stringify(next));
                                                } catch {}
                                            }}
                                        />
                                        {col.label}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ overflow: 'auto', height: 'calc(100% - 38px)', borderTop: '1px solid #E8EAED' }}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: activeGridTemplate,
                            minWidth: 760,
                            height: 30,
                            alignItems: 'center',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            background: '#F8FAFC',
                            borderBottom: '1px solid #E8EAED',
                        }}
                    >
                        {activeColumns.map(column => (
                            <button
                                key={column.key}
                                type="button"
                                onClick={() => handleSort(column.key)}
                                title={column.title}
                                style={{
                                    border: 0,
                                    background: 'transparent',
                                    padding: '0 8px',
                                    textAlign: column.align,
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#5F6368',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {column.label}{sort.key === column.key ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                            </button>
                        ))}
                    </div>

                    {rows.length === 0 ? (
                        <div style={{ padding: '10px 8px', color: '#64748b', fontSize: 12 }}>&lt;no results&gt;</div>
                    ) : groupedRows ? groupedRows.map(group => (
                        <React.Fragment key={`group-fragment-${group.key}`}>
                            <div
                                key={`group-${group.key}`}
                                onClick={() => setExpandedGroups(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: activeGridTemplate,
                                    minWidth: 760,
                                    height: 32,
                                    alignItems: 'center',
                                    borderBottom: '1px solid #E8EAED',
                                    background: '#F0F4F9',
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: '#202124',
                                }}
                            >
                                {activeColumns.map(col => {
                                    if (col.key === 'name') {
                                        return (
                                            <div key={col.key} style={cellStyle}>
                                                <span style={{ marginRight: 4 }}>{expandedGroups[group.key] ? '▾' : '▸'}</span>
                                                {group.name} <span style={{ fontWeight: 400, color: '#5F6368' }}>({group.children.length})</span>
                                            </div>
                                        );
                                    }
                                    if (col.key === 'linkedSent') {
                                        const totalLinked = group.children.reduce((s, r) => s + r.linked, 0);
                                        const totalSent = group.children.reduce((s, r) => s + r.sent, 0);
                                        return <div key={col.key} style={numberCellStyle}>{totalLinked} / {totalSent}</div>;
                                    }
                                    if (col.key === 'completed') {
                                        return <div key={col.key} style={numberCellStyle}>{group.children.reduce((s, r) => s + r.completed, 0)}</div>;
                                    }
                                    if (col.key === 'rate') {
                                        const totalSent = group.children.reduce((s, r) => s + r.sent, 0);
                                        const totalCompleted = group.children.reduce((s, r) => s + r.completed, 0);
                                        return <div key={col.key} style={numberCellStyle}>{totalSent > 0 ? (totalCompleted / totalSent * 100).toFixed(0) + '%' : '—'}</div>;
                                    }
                                    if (col.key === 'lastActivity') {
                                        const latest = Math.max(...group.children.map(r => r.lastActivity || 0));
                                        return <div key={col.key} style={{ ...cellStyle, color: '#5F6368' }}>{formatDate(latest || null)}</div>;
                                    }
                                    if (col.key === 'started') {
                                        return <div key={col.key} style={numberCellStyle}>{group.children.reduce((s, r) => s + (r.started || 0), 0)}</div>;
                                    }
                                    if (col.key === 'abandoned') {
                                        return <div key={col.key} style={numberCellStyle}>{group.children.reduce((s, r) => s + (r.abandoned || 0), 0)}</div>;
                                    }
                                    return <div key={col.key} style={cellStyle}>—</div>;
                                })}
                            </div>
                            {expandedGroups[group.key] && group.children.map((row, index) => {
                                const selected = selectedDeploymentId === row.id;
                                return (
                                    <div
                                        key={`${group.key}-${row.id}`}
                                        onClick={() => handleSelect(row)}
                                        onDoubleClick={() => {
                                            launchExplorer({
                                                surveyId: row.deployment.survey_id,
                                                deploymentId: row.deployment.deployment_id,
                                                tab: 'explore',
                                            }).catch(err => console.error('Open Explorer failed:', err));
                                        }}
                                        onContextMenu={(event) => {
                                            handleSelect(row);
                                            openMenu(event, menuItems(row));
                                        }}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: activeGridTemplate,
                                            minWidth: 760,
                                            height: 32,
                                            alignItems: 'center',
                                            borderBottom: '1px solid #EEF2F7',
                                            background: selected ? '#EBF4FF' : (index % 2 === 1 ? '#FAFBFC' : '#fff'),
                                            cursor: 'pointer',
                                            fontSize: 12,
                                            color: '#202124',
                                        }}
                                    >
                                        {activeColumns.map(col => (
                                            <React.Fragment key={col.key}>{renderCell(row, col, { nameIndent: 20 })}</React.Fragment>
                                        ))}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    )) : rows.map((row, index) => {
                        const selected = selectedDeploymentId === row.id;
                        return (
                            <div
                                key={row.id}
                                onClick={() => handleSelect(row)}
                                onDoubleClick={() => {
                                    launchExplorer({
                                        surveyId: row.deployment.survey_id,
                                        deploymentId: row.deployment.deployment_id,
                                        tab: 'explore',
                                    }).catch(err => console.error('Open Explorer failed:', err));
                                }}
                                onContextMenu={(event) => {
                                    handleSelect(row);
                                    openMenu(event, menuItems(row));
                                }}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: activeGridTemplate,
                                    minWidth: 760,
                                    height: 32,
                                    alignItems: 'center',
                                    borderBottom: '1px solid #EEF2F7',
                                    background: selected ? '#EBF4FF' : (index % 2 === 1 ? '#FAFBFC' : '#fff'),
                                    cursor: 'pointer',
                                    fontSize: 12,
                                    color: '#202124',
                                }}
                            >
                                {activeColumns.map(col => (
                                    <React.Fragment key={col.key}>{renderCell(row, col)}</React.Fragment>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </Panel>
            {contextMenuProps && <ContextMenu {...contextMenuProps} />}
        </>
    );
}

const cellStyle = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '0 8px',
};

const numberCellStyle = {
    ...cellStyle,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
};

function statusColor(status) {
    if (status === 'open') return '#188038';
    return '#A50E0E';
}
